import type {Router} from 'vue-router';

/**
 * Перезагрузка страницы, когда после деплоя не находится чанк прошлой сборки.
 *
 * Открытая вкладка держит старый index.html со старыми хэшами в именах чанков, а в контейнере
 * после деплоя лежат только новые — ленивый import() падает. Перезагрузка подтягивает свежий
 * index.html. Кулдаун в sessionStorage не даёт зациклиться, если чанк не грузится по другой
 * причине (сеть, блокировщик): тогда ошибка уходит дальше как обычно.
 */
const RELOAD_KEY = 'chunk-reload-at';
const RELOAD_COOLDOWN_MS = 10_000;

// Формулировки Chrome, Safari и Firefox для упавшего import(), плюс ошибки preload-хелпера Vite
const CHUNK_ERROR_RE = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|Failed to load module script/i;

function isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return CHUNK_ERROR_RE.test(message);
}

function tryReload(url: string): boolean {
    try {
        const last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
        if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
        // sessionStorage недоступен — без защиты от цикла не перезагружаем
        return false;
    }
    window.location.assign(url);
    return true;
}

export function installChunkReload(router: Router): void {
    // Vite шлёт событие, когда не загрузился динамический модуль или его CSS/JS-зависимости.
    // preventDefault гасит ошибку — делаем это, только если действительно уходим на перезагрузку.
    window.addEventListener('vite:preloadError', (event) => {
        if (tryReload(window.location.href)) event.preventDefault();
    });

    // Ленивые страницы роутера; перезагружаемся сразу на целевой маршрут, а не на текущий.
    router.onError((error, to) => {
        if (isChunkLoadError(error)) tryReload(router.resolve(to).href);
    });
}
